import type { GlbDatumSlotKind, GlbDatumSlotTargetDescriptor } from '../../imported-model/datum-slot';

/** 测量无边界几何稳定 ID 前缀。 */
export const MEASURE_UNBOUNDED_OBJECT_ID_PREFIX = '__measure_unbounded__';

/**
 * 无边界测量几何种类。
 */
export enum MeasureUnboundedGeometryKind {
    /** GLB datum-slot 默认平面。 */
    GlbDatumSlotPlane = 'glb-datum-slot-plane',
    /** GLB datum-slot 原点。 */
    GlbDatumSlotOrigin = 'glb-datum-slot-origin',
    /** 正式基准面。 */
    FormalDatumPlane = 'formal-datum-plane',
    /** 正式基准轴。 */
    FormalDatumAxis = 'formal-datum-axis',
    /** 正式基准点。 */
    FormalDatumPoint = 'formal-datum-point',
}

/**
 * 无边界测量几何绑定。
 *
 * 设计说明：
 * 1. datum-slot 等 info-only 目标没有持久 Object3D.uuid，必须使用稳定 measureObjectId；
 * 2. 正式基准仍保留 scene objectId，binding 仅用于统一求距/浮窗语义。
 */
export interface MeasureUnboundedGeometryBinding {
    /** 无边界几何种类。 */
    kind: MeasureUnboundedGeometryKind;
    /** 测量 SSOT 稳定 objectId。 */
    measureObjectId: string;
    /** 展示名称。 */
    displayName: string;
    /** GLB datum-slot 描述符。 */
    datumSlot?: GlbDatumSlotTargetDescriptor;
    /** 正式基准面 ID。 */
    datumPlaneId?: number;
    /** 正式基准轴 ID。 */
    datumAxisId?: number;
    /** 正式基准点 ID。 */
    datumPointId?: number;
}

/**
 * 构建 GLB datum-slot 测量稳定 objectId。
 *
 * @param descriptor datum-slot 描述符。
 * @returns 稳定 objectId。
 */
export function buildGlbDatumSlotMeasureObjectId(
    descriptor: GlbDatumSlotTargetDescriptor,
): string {
    return `${MEASURE_UNBOUNDED_OBJECT_ID_PREFIX}/glb-datum-slot/${descriptor.tabId}/${descriptor.hostObjectId}/${descriptor.slotKind}`;
}

/**
 * 判断 objectId 是否为测量无边界稳定键。
 *
 * @param objectId 对象 ID。
 * @returns 是否为无边界测量 objectId。
 */
export function isMeasureUnboundedObjectId(objectId: string): boolean {
    return objectId.startsWith(`${MEASURE_UNBOUNDED_OBJECT_ID_PREFIX}/`);
}

/**
 * 解析 datum-slot 槽位中文标签。
 *
 * @param slotKind 槽位类型。
 * @returns 中文标签。
 */
export function resolveGlbDatumSlotDisplayName(slotKind: GlbDatumSlotKind): string {
    switch (slotKind) {
        case 'front-plane':
            return '前视基准面';
        case 'top-plane':
            return '上视基准面';
        case 'right-plane':
            return '右视基准面';
        case 'origin':
            return '原点';
        default:
            return '基准';
    }
}
